import { i18n } from "shared";
import { theme } from "shared/src/theme";
import { styled } from "styled-components";
import { useTranslation } from "next-i18next";
import LinkIcon from "../../assets/link.svg";
import Head from "next/head";
import { Menu } from "antd";
import { useEffect, useRef, useState } from "react";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { debounceFunction } from "../../utils/debounce";

const Wrapper = styled.div`
  max-width: 1440px;
  margin-left: auto;
  margin-top: 160px;
  display: flex;
  margin-right: auto;

  & h1 {
    ${theme.typography.headline1}
    margin-bottom: 32px;
  }
  & h2 {
    ${theme.typography.headline2}
    margin-bottom: 16px;
  }

  & p {
    ${theme.typography.body}
    color: ${theme.gray1};
    margin-bottom: 64px;
    white-space: break-spaces;
  }

  & .m-32 {
    margin-bottom: 32px;
  }

  & a {
    color: ${theme.info};
    display: flex;
    align-items: center;
    margin-top: 16px;

    & svg {
      margin-left: 4px;
    }
    &:hover {
      color: ${theme.buttonRed};
      & path {
        fill: ${theme.buttonRed};
      }
    }
  }
  @media (max-width: 1550px) {
    padding: 0px 5%;
  }
  @media (max-width: 1440px) {
    padding: 0px 9.02% 0px 8.33%;
  }
  @media (max-width: 1245px) {
    padding: 0px 5%;
  }
  @media (max-width: 1150px) {
    padding: 0px 3%;
  }
  @media (max-width: 768px) {
    padding: 0px 16px;

    & .with-links {
      margin-bottom: 91px;
    }
  }

  @media (max-width: 360px) {
    margin-top: 100px;

    & h1 {
      font-size: 26px;
      line-height: 32px;
    }

    & h2 {
      font-size: 20px;
      line-height: 28px;
    }

    & .with-links {
      margin-bottom: 64px;
    }
    & p {
      font-size: 14px;
    }
  }
`;

const Sidebar = styled.div`
  margin-right: 22px;
  min-width: 30%;

  & .ant-menu-item {
    height: auto;
    min-height: 40px;
    padding-bottom: 10px;
    padding-top: 10px;
  }
  & .ant-menu-title-content {
    white-space: normal;
  }
  & ul {
    position: sticky;
    top: 160px;
    height: calc(100vh - 140px);
    overflow-y: scroll;
    overflow-x: hidden;
  }

  @media (max-width: 1175px) {
    & .ant-menu-item {
      padding-bottom: 10px;
    }
  }

  @media (max-width: 915px) {
    display: none;
  }
`;

const Content = styled.div`
  & span {
    color: ${theme.buttonRed};
    font-size: 20px;
  }
  & .link {
    @media (max-width: 340px) {
      font-size: 13px;
    }
  }
`;

const KeihanshinGlobalHubCity = () => {
  const { t } = useTranslation("common");
  const [selectedMenu, setSelectedMenu] = useState("menu0");

  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);

  const sectionRefs = [
    { ref: ref1, section: "menu0" },
    { ref: ref2, section: "menu1" },
    { ref: ref3, section: "menu2" },
  ];

  const MENU = [
    {
      key: "menu0",
      label: "KEIHANSHIN STARTUP ACADEMIA COALITION（KSAC）",
    },
    {
      key: "menu1",
      label: "Kansai innovation initiative (KSII)",
    },
    {
      key: "menu2",
      label: "Osaka / Kyoto / Kobe 3 Chamber of Commerce Business",
    },
  ];

  const scrollToId = (id) => {
    const view = process.browser && document.getElementById(id);
    if (view) {
      const yOffset = -200;
      const y = view.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const handleScroll = () => {
    const negativeOffsetY = 450;
    const scrollPosition = window.scrollY + negativeOffsetY;

    if (scrollPosition < 0) {
      setSelectedMenu("-");
      return;
    }
    const getDimensions = (ele) => {
      const { height } = ele.getBoundingClientRect();

      const offsetTop = ele.offsetTop;
      const offsetBottom = offsetTop + height;

      return {
        height,
        offsetTop,
        offsetBottom,
      };
    };

    const selected = sectionRefs.find(({ ref }) => {
      const ele = ref.current;
      if (ele) {
        const { offsetBottom, offsetTop } = getDimensions(ele);

        return scrollPosition > offsetTop && scrollPosition < offsetBottom;
      }
    });

    if (selected && selected.section !== selectedMenu) {
      setSelectedMenu(selected.section);
    }
  };
  const debouncedScroll = () => {
    debounceFunction(handleScroll);
  };
  useEffect(() => {
    debounceFunction(handleScroll);
    window.addEventListener("scroll", debouncedScroll);
    return () => {
      window.removeEventListener("scroll", debouncedScroll);
    };
  }, [selectedMenu]);

  return (
    <Wrapper>
      <Head>
        <title>
          {t("Keihanshin Global Hub City - Kansai Startup Ecosystem")}
        </title>
        <meta
          name="keywords"
          content="Keihanshin-Global-Hub-City,Deeptech valley,Deeptech,関西ディープテック、関西SU,関西スタートアップ,関西スタートアップエコシステム,関西広域連合,kansai startup,japan kansai startup ecosystem,kansai-ecosystem,deeptech kansai"
        />
        <meta
          key="keihanshin_description"
          name="description"
          content={t(
            "The platform aims to build an ecosystem that creates world-class startups by increasing the number of aspiring entrepreneurs and continuously creating university-launched startups in the Kyoto-Osaka-Hyogo＆Kobe（KEIHANSHIN）area through collaboration beyond regional and organizational boundaries to create a virtuous cycle of human resources, research projects, and funding."
          )}
        />
        <meta
          name="og:description"
          content={t(
            "The platform aims to build an ecosystem that creates world-class startups by increasing the number of aspiring entrepreneurs and continuously creating university-launched startups in the Kyoto-Osaka-Hyogo＆Kobe（KEIHANSHIN）area through collaboration beyond regional and organizational boundaries to create a virtuous cycle of human resources, research projects, and funding."
          )}
        />
      </Head>
      <Sidebar>
        <Menu mode="inline" className="side-menu" selectedKeys={[selectedMenu]}>
          {MENU.map((menu) => (
            <Menu.Item key={menu.key} onClick={() => scrollToId(menu.key)}>
              {t(menu.label)}
            </Menu.Item>
          ))}
        </Menu>
      </Sidebar>
      <Content>
        <div ref={ref1} id="menu0">
          <h1>{t("KEIHANSHIN STARTUP ACADEMIA COALITION（KSAC）")}</h1>

          <p>
            {t(
              "The platform aims to build an ecosystem that creates world-class startups by increasing the number of aspiring entrepreneurs and continuously creating university-launched startups in the Kyoto-Osaka-Hyogo＆Kobe（KEIHANSHIN）area through collaboration beyond regional and organizational boundaries to create a virtuous cycle of human resources, research projects, and funding."
            )}
            <br />
            {t(
              "More than 50 institutions from universities, industry, finance, and local governments in the Kyoto-Osaka-Hyogo＆Kobe（KEIHANSHIN）area region are participating in this project."
            )}
            <br />
            <a
              href="https://ksac.site/"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://ksac.site/ <LinkIcon />
            </a>
          </p>
        </div>
        <div ref={ref2} id="menu1">
          <h1>{t("Kansai innovation initiative (KSII)")}</h1>
          {i18n.language.startsWith("ja") ? (
            <p className="with-links">
              {
                "経済産業省「産学融合先導モデル拠点創出プログラム」の採択を受け、関西圏の強みである、特色を持つ複数の大学と公的研究機関・産業支援機関、そして企業、経済団体、金融機関、ベンチャーキャピタルなどの投資機関、さらに地方自治体などを含めたマルチステークホルダーによる産学官連携のネットワーク創設及びネットワークから生み出される産学融合の研究開発・事業創出の取組を加速化させることを目的にイノベーション創出基盤を整備する。\n\n産官学金75機関が参画（代表幹事機関：公益財団法人都市活力研究所）。"
              }
              <a
                href="https://ksii.jp/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://ksii.jp/ <LinkIcon />
              </a>
            </p>
          ) : (
            <p>
              {'Selected for METI\'s "Program for Creating a Leading Model Center for Industry-Academia Fusion".\n' +
                "We will establish an innovation creation infrastructure.\n" +
                "The objective is to accelerate the creation of a network of industry-academia-government collaboration and the initiatives for R&D and business creation through the fusion of industry and academia generated by the network.\n\n" +
                "Industry-Academia-Government Collaboration:\n"}
              <span>●</span>{" "}
              {
                "Several universities, public research institutes, and industrial support organizations are the strength and characteristics of the Kansai region\n"
              }
              <span>●</span>{" "}
              {
                "Corporations, economic organizations, financial institutions, venture capital, and other investment institutions\n"
              }
              <span>●</span>{" "}
              {
                "Multi-stakeholders including companies, economic organizations, financial institutions, venture capital and other investment institutions, local governments, etc.\n\n"
              }
              {
                "75 industry-government-academia institutions will participate（Representative: Public Interest Incorporated Foundation Urban innovation Institute）"
              }
              <a
                href="https://ksii.jp/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://ksii.jp/ <LinkIcon />
              </a>
            </p>
          )}
        </div>
        <div ref={ref3} id="menu2">
          <h1>{t("Osaka / Kyoto / Kobe 3 Chamber of Commerce Business")}</h1>
          <h2>
            {t("Opening of support company desk for 3 areas of Keihanshin")}
          </h2>
          <p className="m-32">
            {t(
              "In cooperation with the Osaka Industry Bureau, each Chamber of Commerce and Industry of Keihanshin will disseminate startup support information such as information on existing companies that are proactive in collaborating with startups and contribute to the dissemination of information on the global hub cities. Companies in different industries are registered with us from Kyoto, Osaka, and Kobe. (14 companies as of December 2022)"
            )}
          </p>
          <h2>
            {t(
              "Holding a matching meet between existing companies and start-up companies"
            )}
          </h2>
          <p>
            {t(
              "Support startups by providing opportunities for start-up companies to introduce their businesses to existing companies."
            )}
            <a
              href="https://www.starecokansai.com/keihanshin/"
              target="_blank"
              className="link"
              rel="noopener noreferrer"
            >
              https://www.starecokansai.com/keihanshin/ <LinkIcon />
            </a>
          </p>
        </div>
      </Content>
    </Wrapper>
  );
};

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["common"])),
    },
  };
}

export default KeihanshinGlobalHubCity;
